import PropTypes from 'prop-types';
import Link from 'next/link';
import { Button, Grid } from '@ui-propulsion/react-components';
import parse from 'html-react-parser';
import { useEffect, useState } from 'react';
import styles from './twoColumnTextImageDesktop.module.scss';
import handleCTAClick from '../../../services/helpers/handleCTAClick';
import { ctaTheme } from '../../../utils/util';
import SmartImage from '../../smartImage/smartImage';

export default function TwoColumnTextImageDesktop({
  title = '',
  description = '',
  images = [],
  cta = null,
  textBoxPosition = '',
  ctaType = '',
  imageDirection = 'horizontal',
  theme = 'white',
  imageColumnSize = '2',
  imagePosition = 'center',
  imageVerticalAlignment = 'middle',
}) {
  const [gridSizeImageColumn, setGridSizeImageColumn] = useState({});
  const [gridSizeTextColumn, setGridSizeTextColumn] = useState({});
  const [ctaThemes, setCtaThemes] = useState(styles.redOutline);

  useEffect(() => {
    if (cta?.ctaTheme) setCtaThemes(ctaTheme(cta?.ctaTheme));
  }, [cta]);

  useEffect(() => {
    const defaultGridSize = { md: 6, lg: 6, sm: 6 };
    if (imageColumnSize === '1') {
      setGridSizeImageColumn({ md: 4, lg: 4, sm: 4 });
      setGridSizeTextColumn({ md: 8, lg: 8, sm: 8 });
    } else if (imageColumnSize === '2') {
      setGridSizeImageColumn({ md: 6, lg: 6, sm: 6 });
      setGridSizeTextColumn({ md: 6, lg: 6, sm: 6 });
    } else if (imageColumnSize === '3') {
      setGridSizeImageColumn({ md: 8, lg: 8, sm: 8 });
      setGridSizeTextColumn({ md: 4, lg: 4, sm: 4 });
    } else {
      setGridSizeImageColumn(defaultGridSize);
      setGridSizeTextColumn(defaultGridSize);
    }
  }, [imageColumnSize]);
  const imageClass = `${styles.image} ${styles[imageDirection]}`;
  const imageHorizontalAlignClass = `${styles[imagePosition]}`;
  const imageVerticalAlignClass = `${styles[imageVerticalAlignment]}`;
  const imageSectionClass = `${imageHorizontalAlignClass} ${imageVerticalAlignClass}`;
  const contentPaddingClass = `${styles.content} ${
    textBoxPosition === 'left'
      ? styles.contentPaddingRight
      : styles.contentPaddingLeft
  } `;
  const ctaButton = cta && (
    <div className={`${styles.ctaContainer} ${styles[ctaType]}`}>
      <Button
        className={ctaThemes}
        aria-label="radius-bank"
        data-testid="cta"
        variant="tertiary"
        type="button"
        fullWidth
        onClick={(e) => handleCTAClick(e, cta)}
        href={cta?.link}
        id="radius_module_cta"
      >
        {cta?.label}
      </Button>
    </div>
  );
  const textSection = (
    <Grid
      item
      container
      {...gridSizeTextColumn}
      className={contentPaddingClass}
    >
      {title && <h2 className={styles.title}>{parse(title)}</h2>}
      {description && (
        <div className={styles.description}>{parse(description)}</div>
      )}
      {cta && <div className={styles.desktopLayout}>{ctaButton}</div>}
    </Grid>
  );
  const imageSection = (
    <Grid
      item
      container
      data-testid="image-section"
      className={imageSectionClass}
      {...gridSizeImageColumn}
    >
      <div className={imageClass}>
        {images?.map((img, ci) =>
          img?.link ? (
            <Link href={img?.link} key={`row-${ci}`.toString()}>
              <SmartImage
                src={img?.src}
                alt={img?.alt}
                loading="lazy"
                width={img.width}
                height={img.height}
              />
            </Link>
          ) : (
            <SmartImage
              src={img?.src}
              alt={img?.alt}
              loading="lazy"
              key={`row-${ci}`.toString()}
              width={img.width}
              height={img.height}
            />
          )
        )}
      </div>
    </Grid>
  );

  return (
    <section>
      <div className={`${styles.info} ${styles[theme]}`}>
        <Grid container className={styles.twoColumnTextImageDesktopGrid}>
          {textBoxPosition === 'left' ? textSection : imageSection}
          {textBoxPosition === 'left' ? imageSection : textSection}
        </Grid>
      </div>
    </section>
  );
}

TwoColumnTextImageDesktop.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  images: PropTypes.arrayOf(
    PropTypes.shape({
      path: PropTypes.string,
      alt: PropTypes.string,
      link: PropTypes.string,
    })
  ),
  cta: PropTypes.shape({
    label: PropTypes.string,
    link: PropTypes.string,
  }),
  textBoxPosition: PropTypes.string,
  ctaType: PropTypes.string,
  imageDirection: PropTypes.oneOf(['vertical', 'horizontal']),
  theme: PropTypes.string,
  imageColumnSize: PropTypes.string,
  imagePosition: PropTypes.string,
  imageVerticalAlignment: PropTypes.string,
};
